/**
 * KAN-776: предпросмотр партии архивов (import-batch/plan/): таблица файлов
 * (номер, страна, 2FA, прокси, причина пропуска или ошибка разбора) и сводка
 * по странам. Файлы, отклонённые на клиенте (не zip, пустые, большие), в конце
 * таблицы с причиной. Что входит в партию, решает isImportable.
 * KAN-777: строки-дубли серые, с бейджем "дубль" у имени файла; в столбце
 * "Причина" какой файл или метка кабинета повторены.
 */
import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PROXY_UNKNOWN_COUNTRY_MARK,
  describeBatchItemError,
  describePlanSkip,
  hasUnknownCountryProxy,
  isDuplicate,
  isImportable,
  pluralAccounts,
  type RejectedArchive,
} from '@/lib/telegramImportBatch';
import type {
  BatchPlanItem,
  BatchPlanSummary as BatchPlanSummaryData,
} from '@/types/telegramAccount';

interface Props {
  items: BatchPlanItem[];
  rejected: RejectedArchive[];
  pending: boolean;
  error: string | null;
}

/** Столбец "Прокси": метка прокси у строки, которая войдёт в партию; иначе "-". */
function proxyCell(item: BatchPlanItem): { text: string; warn: boolean } {
  if (!isImportable(item)) return { text: '-', warn: false };
  const label = item.proxy_label || '-';
  // прокси назначен, но страна прокси в справочнике не распознана
  if (hasUnknownCountryProxy(item)) {
    return { text: `${label} (${PROXY_UNKNOWN_COUNTRY_MARK})`, warn: true };
  }
  return { text: label, warn: false };
}

/** Столбец "Причина": ошибка разбора (красным) или причина пропуска (янтарным); пусто - строка войдёт. */
function reasonCell(item: BatchPlanItem): { text: string; className: string | undefined } {
  const itemError = describeBatchItemError(item);
  if (itemError) return { text: itemError, className: 'text-red-600' };
  const skip = describePlanSkip(item);
  if (skip) return { text: skip, className: 'text-amber-700' };
  return { text: '', className: undefined };
}

/** Столбец "2FA": сервер мог не определить (null). */
function twoFaText(value: boolean | null): string {
  if (value === null) return '-';
  return value ? 'есть' : 'нет';
}

export function BatchPlanTable({ items, rejected, pending, error }: Props): JSX.Element {
  if (pending) {
    return (
      <p className="flex items-center text-sm text-gray-500" data-testid="batch-plan-pending">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Разбираем архивы...
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {error && (
        <p className="text-sm text-red-600" data-testid="batch-plan-error">
          Не удалось разобрать архивы: {error}
        </p>
      )}
      <Table data-testid="batch-plan-table" className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead>Файл</TableHead>
            <TableHead>Номер</TableHead>
            <TableHead>Страна</TableHead>
            <TableHead>2FA</TableHead>
            <TableHead>Прокси</TableHead>
            <TableHead>Причина</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const proxy = proxyCell(item);
            const reason = reasonCell(item);
            const duplicate = isDuplicate(item);
            return (
              <TableRow
                key={item.index}
                data-testid={`batch-plan-row-${item.index}`}
                className={isImportable(item) ? undefined : 'text-gray-400'}
              >
                <TableCell className="max-w-[12rem]" title={item.file_name}>
                  <span className="inline-flex max-w-full items-center gap-1">
                    <span className="truncate">{item.file_name}</span>
                    {duplicate && (
                      <span
                        className="shrink-0 rounded border border-gray-300 px-1 text-[10px] uppercase leading-4 text-gray-500"
                        data-testid={`batch-plan-dup-badge-${item.index}`}
                      >
                        дубль
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell>{item.phone || '-'}</TableCell>
                <TableCell>{item.country || '-'}</TableCell>
                <TableCell>{twoFaText(item.two_fa_present)}</TableCell>
                <TableCell className={proxy.warn ? 'text-amber-700' : undefined}>
                  {proxy.text}
                </TableCell>
                <TableCell
                  className={reason.className}
                  data-testid={`batch-plan-reason-${item.index}`}
                >
                  {reason.text}
                </TableCell>
              </TableRow>
            );
          })}
          {rejected.map((r, i) => (
            <TableRow
              key={`rejected-${r.file.name}-${i}`}
              data-testid={`batch-plan-rejected-${i}`}
              className="text-gray-400"
            >
              <TableCell className="max-w-[10rem] truncate" title={r.file.name}>
                {r.file.name}
              </TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell className="text-red-600">{r.problem}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Сводка: по странам "UZ: 2 аккаунта, прокси: 2, без прокси: 0" и итог по партии. */
export function BatchPlanSummary({
  summary,
}: {
  summary: BatchPlanSummaryData | null | undefined;
}): JSX.Element | null {
  if (!summary || !summary.countries.length) return null;
  return (
    <ul className="space-y-0.5 text-xs text-gray-600" data-testid="batch-plan-summary">
      {summary.countries.map((s) => (
        <li key={s.country || '-'} data-testid={`batch-plan-summary-${s.country || 'none'}`}>
          <span className="font-medium">{s.country || 'без страны'}</span>: {pluralAccounts(s.accounts)},
          прокси: {s.proxies_used},{' '}
          <span className={s.without_proxy ? 'text-amber-700' : undefined}>
            без прокси: {s.without_proxy}
          </span>
        </li>
      ))}
      <li className="pt-0.5 text-gray-500" data-testid="batch-plan-summary-total">
        Итого: {pluralAccounts(summary.accounts)}, прокси: {summary.proxies_used}, без прокси:{' '}
        {summary.without_proxy}
      </li>
    </ul>
  );
}
